import type { AdminUserStatusChange } from '@carcheck/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSetUserStatusAction } from '../../../api/admin-users';
import { publishToast } from '../../../components/feedback';

/**
 * Encapsulates the suspend / reactivate orchestration for the user detail
 * drawer (AUT-124): fires the status PATCH, a success / error toast, and
 * invalidates the `['admin','users']` cache so the list + drawer refetch.
 * Extracted from the drawer so the body stays under the line cap. The
 * action is role + permission + audit gated server-side (AUT-138 removed
 * per-action TOTP step-up).
 */
export interface UseUserStatusActionsResult {
  /** True while the mutation is in flight. */
  busy: boolean;
  /** Run a status change; resolves true on success, false on error. */
  run: (id: string, next: AdminUserStatusChange, reason: string) => Promise<boolean>;
}

export const useUserStatusActions = (): UseUserStatusActionsResult => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const { action, invalidate } = useSetUserStatusAction();

  const run = async (id: string, next: AdminUserStatusChange, reason: string): Promise<boolean> => {
    setBusy(true);
    const isSuspend = next === 'suspended';
    try {
      await action({ id, status: next, reason });
      await invalidate();
      publishToast({
        severity: 'success',
        durationMs: 4000,
        message: t(
          isSuspend ? 'users.suspendDialog.successToast' : 'users.reactivateDialog.successToast',
        ),
      });
      return true;
    } catch {
      publishToast({
        severity: 'error',
        durationMs: 6000,
        message: t(
          isSuspend ? 'users.suspendDialog.errorToast' : 'users.reactivateDialog.errorToast',
        ),
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};
